/**
 * Auto-Fill Pattern Configuration
 *
 * Multi-language keyword patterns for automatically populating PCF attributes
 * based on Revit family names. Supports English, German, French, Spanish,
 * Hungarian, Russian, and Chinese keywords.
 */

/**
 * Pattern interface for mapping keywords to PCF attributes
 */
export interface AutoFillPattern {
  /** Unique identifier for the pattern */
  id: string;
  /** Multi-language keywords that trigger this pattern */
  keywords: string[];
  /** PCF OBJECT-TYPE value */
  objectType: string;
  /** Default SKEY pattern (use ?? for angle placeholder) */
  skey: string;
  /** Optional default END-TYPE */
  endType?: string;
  /** Priority - higher values take precedence in matches (0-100) */
  priority: number;
  /** Category for grouping */
  category: 'fitting' | 'valve' | 'pipe' | 'support' | 'instrument' | 'misc';
}

/**
 * END-TYPE pattern for detecting connection types
 */
export interface EndTypePattern {
  /** Keywords that indicate this end type */
  keywords: string[];
  /** PCF END-TYPE value */
  endType: string;
  /** Priority for matching */
  priority: number;
}

// ============================================================================
// OBJECT-TYPE & SKEY PATTERNS
// Based on PCF Manual Section 9.2 "SKEY vs OBJECT TYPE"
// ============================================================================

export const AUTO_FILL_PATTERNS: AutoFillPattern[] = [
  // -------------------------------------------------------------------------
  // ELBOWS (EL??, EB??, ER??, EU??)
  // -------------------------------------------------------------------------
  {
    id: 'elbow-90',
    keywords: [
      // English
      'elbow 90', '90 elbow', '90deg elbow', '90° elbow', 'el90',
      // German
      'bogen 90', '90° bogen', '90-grad-bogen',
      // Russian
      'колено 90', 'отвод 90', '90° колено',
      // Spanish
      'codo 90', '90° codo',
      // French
      'coude 90', '90° coude',
      // Hungarian
      '90 fokos könyök', '90 könyök',
      // Chinese
      '90度弯头', '90°弯头'
    ],
    objectType: 'ELBOW',
    skey: 'EL90',
    priority: 95,
    category: 'fitting'
  },
  {
    id: 'elbow-45',
    keywords: [
      'elbow 45', '45 elbow', '45deg elbow', '45° elbow', 'el45',
      'bogen 45', '45° bogen', '45-grad-bogen',
      'колено 45', 'отвод 45', '45° колено',
      'codo 45', '45° codo',
      'coude 45', '45° coude',
      '45 fokos könyök', '45 könyök',
      '45度弯头', '45°弯头'
    ],
    objectType: 'ELBOW',
    skey: 'EL45',
    priority: 95,
    category: 'fitting'
  },
  {
    id: 'elbow-180-return',
    keywords: [
      'return elbow', '180 elbow', 'elbow 180', '180° elbow', 'u-bend elbow',
      'rückbogen', '180° bogen',
      'калач', 'колено 180', 'возвратное колено',
      'codo 180', 'codo de retorno',
      'coude 180', 'coude de retour',
      '180 fokos könyök', 'visszafordító könyök',
      '180度弯头', 'U型弯头'
    ],
    objectType: 'ELBOW',
    skey: 'EU??',
    priority: 90,
    category: 'fitting'
  },
  {
    id: 'elbow-reducing',
    keywords: [
      'reducing elbow', 'reducer elbow', 'elbow reducing', 'red elbow',
      'reduzierender bogen', 'reduzierbogen',
      'переходное колено', 'колено переходное',
      'codo reductor', 'codo de reducción',
      'coude réducteur',
      'szűkítő könyök', 'redukált könyök',
      '异径弯头', '变径弯头'
    ],
    objectType: 'ELBOW',
    skey: 'ER??',
    priority: 85,
    category: 'fitting'
  },
  {
    id: 'elbow-generic',
    keywords: [
      'elbow', 'ell',
      'bogen', 'rohrbogen', 'winkel',
      'колено', 'отвод', 'угольник',
      'codo', 'curva',
      'coude',
      'könyök', 'csőkönyök',
      '弯头', '肘管', '弯管'
    ],
    objectType: 'ELBOW',
    skey: 'EL??',
    priority: 50,
    category: 'fitting'
  },

  // -------------------------------------------------------------------------
  // BENDS (BE??, PB??, MI??, BU??)
  // -------------------------------------------------------------------------
  {
    id: 'bend-mitered',
    keywords: [
      'mitered bend', 'miter bend', 'mitre bend', 'segmented bend',
      'segmentbogen', 'gehrungsbogen',
      'сварной отвод', 'секторный отвод',
      'codo segmentado', 'curva segmentada',
      'coude à segments',
      '虾米腰弯头', '分段弯头'
    ],
    objectType: 'BEND',
    skey: 'MI??',
    priority: 85,
    category: 'fitting'
  },
  {
    id: 'bend-180-return',
    keywords: [
      'return bend', '180 bend', 'bend 180', '180° bend', 'u-bend',
      'rückbiegung', '180° biegung',
      'гиб 180', 'возвратный гиб',
      'curva 180', 'curva de retorno',
      'cintrage 180',
      '180度弯管'
    ],
    objectType: 'BEND',
    skey: 'BU??',
    priority: 85,
    category: 'fitting'
  },
  {
    id: 'bend-pipe',
    keywords: [
      'pipe bend', 'tube bend',
      'rohrbiegung',
      'гиб трубы',
      'curva de tubo',
      'cintrage de tube',
      'csőív',
      '管弯'
    ],
    objectType: 'BEND',
    skey: 'PB??',
    priority: 60,
    category: 'fitting'
  },
  {
    id: 'bend-generic',
    keywords: [
      'bend', 'long radius bend', 'lr bend',
      'biegung', 'bogen',
      'гиб', 'гнутый отвод',
      'curva',
      'cintrage', 'tube cintré',
      'ív', 'hajlítás',
      '弯管'
    ],
    objectType: 'BEND',
    skey: 'BE??',
    priority: 45,
    category: 'fitting'
  },

  // -------------------------------------------------------------------------
  // TEES & BRANCHES (TE??, TY??, BT??)
  // -------------------------------------------------------------------------
  {
    id: 'tee-equal',
    keywords: [
      'equal tee', 'straight tee', 'tee equal',
      'gleichschenkliges t-stück', 't-stück gleich',
      'равнопроходной тройник', 'тройник равный',
      'te igual', 'te recta',
      'té égal',
      'egyenlő t-idom', 'egyenlő tee',
      '等径三通', '直通三通'
    ],
    objectType: 'TEE',
    skey: 'TEEE',
    priority: 85,
    category: 'fitting'
  },
  {
    id: 'tee-reducing',
    keywords: [
      'reducing tee', 'reduced tee', 'tee reducing',
      'reduzierendes t-stück', 't-stück reduziert',
      'переходной тройник', 'тройник переходной',
      'te reducida', 'te de reducción',
      'té réduit',
      'szűkítő t-idom', 'redukált tee',
      '异径三通', '变径三通'
    ],
    objectType: 'TEE',
    skey: 'TEER',
    priority: 85,
    category: 'fitting'
  },
  {
    id: 'tee-bend',
    keywords: [
      'tee bend', 'bent tee', 'angled tee',
      'gebogenes t-stück',
      'тройник с отводом',
      'te con curva',
      'té coudé',
      'íves t-idom',
      '弯头三通'
    ],
    objectType: 'TEE',
    skey: 'BT??',
    priority: 80,
    category: 'fitting'
  },
  {
    id: 'tee-generic',
    keywords: [
      'tee', 't-piece', 'tee fitting',
      't-stück', 'abzweig',
      'тройник',
      'te', 'derivación',
      'té', 'raccord en t',
      't-idom', 'elágazás',
      '三通', 'T型管件'
    ],
    objectType: 'TEE',
    skey: 'TE??',
    priority: 50,
    category: 'fitting'
  },
  {
    id: 'wye-fitting',
    keywords: [
      'wye', 'y-fitting', 'lateral', 'y-branch',
      'y-stück', 'hosenrohr',
      'штаны', 'y-образный',
      'ye', 'derivación lateral',
      'culotte',
      'y-idom',
      'Y型三通', '斜三通'
    ],
    objectType: 'TEE',
    skey: 'TY??',
    priority: 60,
    category: 'fitting'
  },

  // -------------------------------------------------------------------------
  // TAP-CONNECTION
  // -------------------------------------------------------------------------
  {
    id: 'tap-connection',
    keywords: [
      'tap connection', 'tap', 'branch connection', 'pipe tap',
      'abzweigstutzen', 'anschlussstutzen',
      'врезка', 'отвод врезной',
      'conexión derivada', 'toma',
      'piquage', 'raccordement',
      '支管连接', '分支接头'
    ],
    objectType: 'TAP-CONNECTION',
    skey: '',
    priority: 70,
    category: 'fitting'
  },

  // -------------------------------------------------------------------------
  // OLETS (NI??, HC??, LA??, TH??, WTBW, SKSW)
  // -------------------------------------------------------------------------
  {
    id: 'olet-weldolet',
    keywords: [
      'weldolet', 'weld-o-let', 'welded olet',
      'schweißstutzen',
      'вварной отвод', 'вельдолет',
      'weldolet',
      'weldolet',
      '焊接式支管台'
    ],
    objectType: 'OLET',
    skey: 'WTBW',
    priority: 85,
    category: 'fitting'
  },
  {
    id: 'olet-sockolet',
    keywords: [
      'sockolet', 'sock-o-let', 'socket olet',
      'muffenstutzen',
      'сокколет', 'раструбный отвод',
      'sockolet',
      'sockolet',
      '承插式支管台'
    ],
    objectType: 'OLET',
    skey: 'SKSW',
    priority: 85,
    category: 'fitting'
  },
  {
    id: 'olet-threadolet',
    keywords: [
      'threadolet', 'thread-o-let', 'threaded olet', 'thredolet',
      'gewindestutzen',
      'тредолет', 'резьбовой отвод',
      'threadolet',
      'threadolet',
      '螺纹式支管台'
    ],
    objectType: 'OLET',
    skey: 'TH??',
    priority: 85,
    category: 'fitting'
  },
  {
    id: 'olet-nipolet',
    keywords: [
      'nipolet', 'nip-o-let', 'nipple olet',
      'nippelstutzen',
      'ниполет',
      'nipolet',
      'nipolet',
      '短管支管台'
    ],
    objectType: 'OLET',
    skey: 'NI??',
    priority: 85,
    category: 'fitting'
  },
  {
    id: 'olet-half-coupling',
    keywords: [
      'half coupling olet', 'half-coupling', 'half coupling',
      'halbmuffe',
      'полумуфта',
      'medio acoplamiento',
      'demi-manchon',
      '半联接'
    ],
    objectType: 'OLET',
    skey: 'HC??',
    priority: 80,
    category: 'fitting'
  },
  {
    id: 'olet-latrolet',
    keywords: [
      'latrolet', 'lateral olet', 'lat-o-let', '45 olet',
      'lateralstutzen',
      'латролет', 'косой отвод',
      'latrolet',
      'latrolet',
      '斜接支管台'
    ],
    objectType: 'OLET',
    skey: 'LA??',
    priority: 85,
    category: 'fitting'
  },
  {
    id: 'olet-generic',
    keywords: [
      'olet', 'o-let', 'branch fitting', 'outlet', 'fitting branch',
      'stutzen', 'abzweigung',
      'олет', 'штуцер',
      'olet', 'derivación',
      'olet', 'piquage',
      '支管台'
    ],
    objectType: 'OLET',
    skey: 'WTBW',
    priority: 50,
    category: 'fitting'
  },

  // -------------------------------------------------------------------------
  // REDUCERS
  // -------------------------------------------------------------------------
  {
    id: 'reducer-transition',
    keywords: [
      'transition - generic', 'transition generic',
      'transition - welded', 'transition welded',
      'welded transition', 'pipe transition',
      'reducing transition'
    ],
    objectType: 'REDUCER_CONCENTRIC',
    skey: 'RC??',
    priority: 65,
    category: 'fitting'
  },
  {
    id: 'reducer-concentric',
    keywords: [
      'concentric reducer', 'reducer concentric', 'con reducer',
      'konzentrisches reduzierstück', 'reduzierstück konzentrisch',
      'концентрический переход', 'переход концентрический',
      'reductor concéntrico', 'reducción concéntrica',
      'réducteur concentrique',
      'koncentrikus szűkítő',
      '同心异径管', '同心大小头'
    ],
    objectType: 'REDUCER_CONCENTRIC',
    skey: 'RC??',
    priority: 85,
    category: 'fitting'
  },
  {
    id: 'reducer-eccentric',
    keywords: [
      'eccentric reducer', 'reducer eccentric', 'ecc reducer', 'pi x fitting reducer',
      'exzentrisches reduzierstück', 'reduzierstück exzentrisch',
      'эксцентрический переход', 'переход эксцентрический',
      'reductor excéntrico', 'reducción excéntrica',
      'réducteur excentrique',
      'excentrikus szűkítő',
      '偏心异径管', '偏心大小头'
    ],
    objectType: 'REDUCER_ECCENTRIC',
    skey: 'RE??',
    priority: 85,
    category: 'fitting'
  },
  {
    id: 'reducer-generic',
    keywords: [
      'reducer', 'reduction', 'swage',
      'reduzierstück', 'reduzierung', 'verjüngung',
      'переход', 'редукция',
      'reductor', 'reducción',
      'réducteur', 'réduction',
      'szűkítő', 'redukció',
      '异径管', '大小头', '变径'
    ],
    objectType: 'REDUCER_CONCENTRIC',
    skey: 'RC??',
    priority: 40,
    category: 'fitting'
  },

  // -------------------------------------------------------------------------
  // VALVES - Based on PCF Manual Section 9.2
  // -------------------------------------------------------------------------
  {
    id: 'valve-ball',
    keywords: [
      'ball valve', 'ball-valve', 'ballvalve',
      'kugelventil', 'kugelhahn',
      'шаровой кран', 'шаровый клапан', 'шаровой вентиль',
      'válvula de bola', 'llave de bola',
      'vanne à boule', 'robinet à boisseau sphérique',
      'golyóscsap', 'golyósszelep',
      '球阀', '球形阀'
    ],
    objectType: 'VALVE',
    skey: 'VB??',
    priority: 90,
    category: 'valve'
  },
  {
    id: 'valve-gate',
    keywords: [
      'gate valve', 'gate-valve', 'gatevalve', 'wedge gate',
      'schieber', 'absperrschieber', 'keilschieber',
      'задвижка', 'клиновая задвижка',
      'válvula de compuerta', 'compuerta',
      'vanne à opercule', 'robinet-vanne',
      'tolózár', 'kapuszelep',
      '闸阀', '闸板阀'
    ],
    objectType: 'VALVE',
    skey: 'VT??',
    priority: 90,
    category: 'valve'
  },
  {
    id: 'valve-check',
    keywords: [
      'check valve', 'check-valve', 'non-return valve', 'nrv',
      'rückschlagventil', 'rückschlagklappe',
      'обратный клапан', 'обратка',
      'válvula de retención', 'válvula anti-retorno', 'válvula check',
      'clapet anti-retour', 'clapet de non-retour',
      'visszacsapó szelep',
      '止回阀', '单向阀', '逆止阀'
    ],
    objectType: 'VALVE',
    skey: 'CK??',
    priority: 90,
    category: 'valve'
  },
  {
    id: 'valve-swing-check',
    keywords: [
      'swing check', 'swing check valve', 'swing-check',
      'schwenkklappe', 'pendelrückschlagklappe',
      'поворотный обратный', 'хлопушка',
      'válvula de retención oscilante',
      'clapet battant',
      'lengő visszacsapó szelep',
      '旋启式止回阀'
    ],
    objectType: 'VALVE',
    skey: 'VC??',
    priority: 92,
    category: 'valve'
  },
  {
    id: 'valve-globe',
    keywords: [
      'globe valve', 'globe-valve',
      'ventil', 'absperrventil', 'regulierventil',
      'вентиль', 'запорный вентиль',
      'válvula de globo',
      'vanne à soupape', 'robinet à soupape',
      'glóbuszszelep',
      '截止阀', '球心阀'
    ],
    objectType: 'VALVE',
    skey: 'VG??',
    priority: 85,
    category: 'valve'
  },
  {
    id: 'valve-butterfly',
    keywords: [
      'butterfly valve', 'butterfly-valve', 'wafer valve',
      'absperrklappe', 'drosselklappe',
      'дисковый затвор', 'поворотная заслонка', 'баттерфляй',
      'válvula de mariposa', 'mariposa',
      'vanne papillon',
      'pillangószelep',
      '蝶阀', '蝴蝶阀'
    ],
    objectType: 'VALVE',
    skey: 'VY??',
    endType: 'WF',
    priority: 85,
    category: 'valve'
  },
  {
    id: 'valve-diaphragm',
    keywords: [
      'diaphragm valve', 'diaphragm-valve', 'membrane valve',
      'membranventil',
      'мембранный клапан', 'диафрагменный',
      'válvula de diafragma', 'válvula de membrana',
      'vanne à membrane',
      'membránszelep',
      '隔膜阀'
    ],
    objectType: 'VALVE',
    skey: 'VD??',
    priority: 85,
    category: 'valve'
  },
  {
    id: 'valve-plug',
    keywords: [
      'plug valve', 'cock valve',
      'hahnventil', 'kükenhahn',
      'пробковый кран', 'конусный кран',
      'válvula de tapón', 'llave de paso',
      'robinet à boisseau',
      'dugószelep',
      '旋塞阀', '考克阀'
    ],
    objectType: 'VALVE',
    skey: 'VP??',
    priority: 80,
    category: 'valve'
  },
  {
    id: 'valve-needle',
    keywords: [
      'needle valve', 'needle-valve',
      'nadelventil',
      'игольчатый клапан', 'игольчатый вентиль',
      'válvula de aguja',
      'vanne à pointeau',
      'tűszelep',
      '针型阀', '针阀'
    ],
    objectType: 'VALVE',
    skey: 'NV??',
    priority: 80,
    category: 'valve'
  },
  {
    id: 'valve-relief',
    keywords: [
      'relief valve', 'safety valve', 'pressure relief', 'prv', 'psv',
      'sicherheitsventil', 'überdruckventil',
      'предохранительный клапан', 'клапан сброса давления',
      'válvula de alivio', 'válvula de seguridad',
      'soupape de sécurité', 'soupape de décharge',
      'biztonsági szelep', 'nyomáscsökkentő szelep',
      '安全阀', '泄压阀'
    ],
    objectType: 'VALVE',
    skey: 'VS??',
    priority: 85,
    category: 'valve'
  },
  {
    id: 'valve-control',
    keywords: [
      'control valve', 'regulating valve', 'modulating valve',
      'regelventil', 'stellventil',
      'регулирующий клапан', 'регулятор',
      'válvula de control', 'válvula reguladora',
      'vanne de régulation',
      'szabályozó szelep',
      '调节阀', '控制阀'
    ],
    objectType: 'VALVE',
    skey: 'CV??',
    priority: 85,
    category: 'valve'
  },
  {
    id: 'valve-3way',
    keywords: [
      '3-way valve', '3 way valve', 'three-way valve', 'three way', '3way',
      '3-wege-ventil', 'dreiwegeventil',
      'трехходовой клапан', '3-ходовой',
      'válvula de 3 vías', 'válvula tres vías',
      'vanne 3 voies', 'vanne trois voies',
      '三通阀', '三路阀'
    ],
    objectType: 'VALVE-3WAY',
    skey: 'V3??',
    priority: 92,
    category: 'valve'
  },
  {
    id: 'valve-4way',
    keywords: [
      '4-way valve', '4 way valve', 'four-way valve', 'four way', '4way',
      '4-wege-ventil', 'vierwegeventil',
      'четырехходовой клапан', '4-ходовой',
      'válvula de 4 vías', 'válvula cuatro vías',
      'vanne 4 voies', 'vanne quatre voies',
      '四通阀', '四路阀'
    ],
    objectType: 'VALVE-4WAY',
    skey: 'V4??',
    priority: 92,
    category: 'valve'
  },
  {
    id: 'valve-angle',
    keywords: [
      'angle valve', 'angle-valve', 'angled valve', 'corner valve',
      'eckventil', 'winkelventil',
      'угловой клапан', 'угловой вентиль',
      'válvula angular', 'válvula de ángulo',
      'vanne d\'angle', 'robinet d\'angle',
      'sarokszelep', 'szögszelep',
      '角阀', '角式阀'
    ],
    objectType: 'VALVE-ANGLE',
    skey: 'AV??',
    priority: 85,
    category: 'valve'
  },
  {
    id: 'valve-generic',
    keywords: [
      'valve', 'vlv',
      'ventil', 'armatur',
      'клапан', 'кран', 'арматура',
      'válvula', 'llave',
      'vanne', 'robinet',
      'szelep', 'csap',
      '阀', '阀门'
    ],
    objectType: 'VALVE',
    skey: 'VB??',
    priority: 30,
    category: 'valve'
  },

  // -------------------------------------------------------------------------
  // FLANGES
  // -------------------------------------------------------------------------
  {
    id: 'flange-weld-neck',
    keywords: [
      'weld neck flange', 'welding neck', 'wnrf', 'wn flange', 'flange wn',
      'vorschweißflansch', 'anschweiß-flansch',
      'воротниковый фланец', 'приварной фланец с воротником',
      'brida de cuello soldable', 'brida welding neck',
      'bride à collerette à souder', 'bride a collerette soude', 'collerette soude',
      'hegesztőnyakas karima', 'nyakas karima',
      '对焊法兰', '带颈对焊法兰'
    ],
    objectType: 'FLANGE',
    skey: 'FLWN',
    priority: 90,
    category: 'fitting'
  },
  {
    id: 'flange-slip-on',
    keywords: [
      'slip on flange', 'slip-on', 'sorf', 'so flange',
      'aufsteckflansch', 'lose flansch',
      'плоский фланец', 'приварной плоский фланец',
      'brida slip-on', 'brida deslizable',
      'bride plate à souder',
      '平焊法兰', '板式法兰'
    ],
    objectType: 'FLANGE',
    skey: 'FLSO',
    priority: 90,
    category: 'fitting'
  },
  {
    id: 'flange-blind',
    keywords: [
      'blind flange', 'blank flange', 'blrf', 'flange bl',
      'blindflansch', 'verschlussflansch',
      'заглушка фланцевая', 'глухой фланец',
      'brida ciega', 'tapa de brida',
      'bride pleine', 'bride aveugle',
      'vakkarima',
      '盲板', '法兰盖'
    ],
    objectType: 'FLANGE_BLIND',
    skey: 'FLBL',
    priority: 90,
    category: 'fitting'
  },
  {
    id: 'flange-threaded',
    keywords: [
      'threaded flange', 'screwed flange',
      'gewindeflansch',
      'резьбовой фланец',
      'brida roscada',
      'bride taraudée', 'bride filetée',
      '螺纹法兰'
    ],
    objectType: 'FLANGE',
    skey: 'FLTH',
    priority: 85,
    category: 'fitting'
  },
  {
    id: 'flange-socket-weld',
    keywords: [
      'socket weld flange', 'sw flange',
      'schweißmuffen-flansch',
      'фланец под приварку в раструб',
      'brida socket weld',
      'bride à emboîtement soudé',
      '承插焊法兰'
    ],
    objectType: 'FLANGE',
    skey: 'FLSW',
    priority: 85,
    category: 'fitting'
  },
  {
    id: 'flange-lap-joint',
    keywords: [
      'lap joint flange', 'loose flange', 'stub end',
      'bördelflansch', 'losflansch',
      'свободный фланец', 'накидной фланец',
      'brida lap joint', 'brida loca',
      'bride tournante',
      '松套法兰', '活套法兰'
    ],
    objectType: 'FLANGE',
    skey: 'FLLJ',
    priority: 85,
    category: 'fitting'
  },
  {
    id: 'flange-generic',
    keywords: [
      'flange', 'flg',
      'flansch', 'fl',
      'фланец',
      'brida',
      'bride',
      'karima',
      '法兰'
    ],
    objectType: 'FLANGE',
    skey: 'FLWN',
    priority: 35,
    category: 'fitting'
  },

  // -------------------------------------------------------------------------
  // CAPS & PLUGS
  // -------------------------------------------------------------------------
  {
    id: 'cap-butt-weld',
    keywords: [
      'cap', 'pipe cap', 'butt weld cap', 'bw cap',
      'kappe', 'rohrkappe', 'schweißkappe',
      'заглушка', 'приварная заглушка', 'днище',
      'tapón', 'tapa de tubo',
      'fond bombé', 'bouchon soudé',
      '封头', '管帽'
    ],
    objectType: 'CAP',
    skey: 'KABW',
    priority: 70,
    category: 'fitting'
  },
  {
    id: 'plug-threaded',
    keywords: [
      'plug', 'pipe plug', 'threaded plug', 'hex plug',
      'stopfen', 'verschlussstopfen', 'gewindestopfen',
      'пробка', 'резьбовая пробка',
      'tapón roscado', 'tapón macho',
      'bouchon fileté',
      '丝堵', '管塞'
    ],
    objectType: 'PLUG',
    skey: 'PLSC',
    priority: 70,
    category: 'fitting'
  },

  // -------------------------------------------------------------------------
  // COUPLINGS & UNIONS
  // -------------------------------------------------------------------------
  {
    id: 'coupling-threaded',
    keywords: [
      'coupling', 'pipe coupling', 'threaded coupling', 'socket',
      'muffe', 'gewindemuffe', 'rohrmuffe',
      'муфта', 'резьбовая муфта',
      'acoplamiento', 'cople',
      'manchon', 'raccord',
      '管接头', '套管'
    ],
    objectType: 'COUPLING',
    skey: 'CO??',
    priority: 60,
    category: 'fitting'
  },
  {
    id: 'union',
    keywords: [
      'union', 'pipe union',
      'verschraubung', 'rohrverschraubung',
      'сгон', 'разъемное соединение',
      'unión', 'unión de tubo',
      'union', 'raccord union',
      '活接', '由令'
    ],
    objectType: 'UNION',
    skey: 'UN??',
    priority: 65,
    category: 'fitting'
  },
  {
    id: 'nipple',
    keywords: [
      'nipple', 'pipe nipple', 'barrel nipple',
      'rohrnippel', 'doppelnippel',
      'ниппель', 'бочонок',
      'niple', 'tubería corta',
      'mamelon', 'tube court fileté',
      '短管', '管接'
    ],
    objectType: 'PIPE',
    skey: 'NIPPLE',
    priority: 55,
    category: 'fitting'
  },

  // -------------------------------------------------------------------------
  // SUPPORTS - Based on PCF Manual Section 9.2
  // SUPP, O1HG, ANCH, GUID, SPRG, DUCK, HANG, SKID, PSIG
  // -------------------------------------------------------------------------
  {
    id: 'support-anchor',
    keywords: [
      'anchor', 'fixed point', 'fixed support', 'anchor point',
      'festpunkt', 'fixpunkt', 'festlager',
      'мертвая опора', 'неподвижная опора', 'анкер',
      'anclaje', 'punto fijo',
      'point fixe', 'ancrage',
      '固定支架', '锚点'
    ],
    objectType: 'SUPPORT',
    skey: 'ANCH',
    priority: 75,
    category: 'support'
  },
  {
    id: 'support-guide',
    keywords: [
      'guide', 'pipe guide', 'sliding support', 'line guide', 'pipe shoe', 'pipeshoe',
      'führung', 'gleitführung',
      'направляющая', 'скользящая опора',
      'guía', 'guía deslizante',
      'guide', 'support coulissant',
      'vezető támasz', 'vezető',
      '导向支架', '滑动支架'
    ],
    objectType: 'SUPPORT',
    skey: 'GUID',
    priority: 75,
    category: 'support'
  },
  {
    id: 'support-spring',
    keywords: [
      'spring hanger', 'spring support', 'variable spring', 'constant spring',
      'federhänger', 'federunterstützung',
      'пружинная подвеска', 'пружинная опора',
      'soporte de resorte', 'colgador de resorte',
      'suspension à ressort',
      '弹簧支架', '弹簧吊架'
    ],
    objectType: 'SUPPORT',
    skey: 'SPRG',
    priority: 75,
    category: 'support'
  },
  {
    id: 'support-hanger',
    keywords: [
      'hanger', 'pipe hanger', 'rod hanger', 'clevis hanger',
      'aufhänger', 'rohrhänger',
      'подвеска', 'подвес', 'хомут подвесной',
      'colgador', 'suspensión',
      'suspension', 'pendard',
      'függesztő', 'csőfüggesztő',
      '吊架', '悬挂'
    ],
    objectType: 'SUPPORT',
    skey: 'HANG',
    priority: 70,
    category: 'support'
  },
  {
    id: 'support-dummy-leg',
    keywords: [
      'dummy leg', 'dummy support', 'trunnion', 'dummyleg',
      'rohrschuh', 'gleitschuh',
      'башмак', 'опорная лапа',
      'zapata', 'patín',
      'patin', 'sabot',
      '管鞋', '支撑腿'
    ],
    objectType: 'SUPPORT',
    skey: 'DUCK',
    priority: 70,
    category: 'support'
  },
  {
    id: 'support-skid',
    keywords: [
      'skid', 'pipe skid', 'sliding skid',
      'gleitschiene',
      'салазки', 'опорные салазки',
      'patín deslizante',
      'patin glissant',
      '滑撬'
    ],
    objectType: 'SUPPORT',
    skey: 'SKID',
    priority: 70,
    category: 'support'
  },
  {
    id: 'support-on-support',
    keywords: [
      'support on support', 'secondary support', 'pipe on support',
      'sekundärstütze',
      'опора на опоре',
      'soporte sobre soporte',
      'support sur support',
      '支架上的支架'
    ],
    objectType: 'SUPPORT',
    skey: 'PSIG',
    priority: 65,
    category: 'support'
  },
  {
    id: 'support-general',
    keywords: [
      'general support', 'standard support',
      'standardstütze',
      'стандартная опора',
      'soporte general',
      'support général',
      'általános támasz',
      '通用支架'
    ],
    objectType: 'SUPPORT',
    skey: 'O1HG',
    priority: 55,
    category: 'support'
  },
  {
    id: 'support-generic',
    keywords: [
      'support', 'pipe support', 'bracket', 'rest',
      'stütze', 'rohrstütze', 'halterung',
      'опора', 'кронштейн',
      'soporte', 'abrazadera',
      'support', 'collier',
      'támasz', 'csőtartó',
      '支架', '管托'
    ],
    objectType: 'SUPPORT',
    skey: 'SUPP',
    priority: 50,
    category: 'support'
  },

  // -------------------------------------------------------------------------
  // PIPE
  // -------------------------------------------------------------------------
  {
    id: 'pipe-generic',
    keywords: [
      'pipe', 'tube', 'piping', 'conduit',
      'rohr', 'rohrleitung', 'leitung',
      'труба', 'трубопровод',
      'tubo', 'tubería', 'conducto',
      'tube', 'tuyau', 'conduite',
      '管', '管道', '钢管'
    ],
    objectType: 'PIPE',
    skey: '',
    priority: 20,
    category: 'pipe'
  },

  // -------------------------------------------------------------------------
  // INSTRUMENTS
  // -------------------------------------------------------------------------
  {
    id: 'orifice',
    keywords: [
      'orifice', 'orifice plate', 'orifice flange',
      'messblende', 'blendenflansch',
      'диафрагма', 'измерительная шайба',
      'orificio', 'placa de orificio',
      'plaque à orifice', 'diaphragme',
      '孔板', '节流孔板'
    ],
    objectType: 'ORIFICE',
    skey: 'ORIF',
    priority: 75,
    category: 'instrument'
  },
  {
    id: 'flow-meter',
    keywords: [
      'flow meter', 'flowmeter', 'flow element',
      'water meter', 'water meter unit',
      'durchflussmesser', 'strömungsmesser',
      'расходомер', 'измеритель расхода',
      'medidor de flujo', 'caudalímetro',
      'débitmètre',
      'áramlásmérő',
      '流量计'
    ],
    objectType: 'INSTRUMENT',
    skey: 'FLOW',
    endType: 'FL',
    priority: 70,
    category: 'instrument'
  },
  {
    id: 'strainer',
    keywords: [
      'strainer', 'y-strainer', 't-strainer', 'filter',
      'sieb', 'schmutzfänger', 'filter',
      'фильтр', 'грязевик', 'сетчатый фильтр',
      'colador', 'filtro', 'tamiz',
      'filtre', 'crépine',
      '过滤器', 'Y型过滤器'
    ],
    objectType: 'FILTER',
    skey: 'FILT',
    priority: 70,
    category: 'instrument'
  },
  {
    id: 'trap',
    keywords: [
      'steam trap', 'thermostatic trap', 'disc steam trap', 'clean steam trap', 'trap',
      'kondensatableiter', 'purgeur de vapeur', 'trampa de vapor', 'gőzcsapda', 'конденсатоотводчик'
    ],
    objectType: 'MISC-COMPONENT',
    skey: 'TI??',
    priority: 82,
    category: 'instrument'
  },
  {
    id: 'instrument-generic',
    keywords: [
      'instrument', 'instrumentation', 'pressure gauge', 'temperature transmitter',
      'gauge', 'transmitter', 'druckmessgerat', 'manometre', 'instrumento', 'manómetro',
      'transmisor', 'műszer', 'nyomásmérő', 'adó', 'прибор', 'манометр', 'датчик', 'передатчик',
      '仪表', '变送器'
    ],
    objectType: 'INSTRUMENT',
    skey: 'ID??',
    priority: 45,
    category: 'instrument'
  },

  // -------------------------------------------------------------------------
  // MISCELLANEOUS - Based on PCF Manual Section 9.2
  // -------------------------------------------------------------------------
  {
    id: 'flow-arrow',
    keywords: [
      'flow direction', 'flowdirection', 'flow arrow', 'direction arrow',
      'sens de flux', 'direction de flux'
    ],
    objectType: 'FLOW-ARROW',
    skey: 'FLOW',
    priority: 80,
    category: 'misc'
  },
  {
    id: 'floor-symbol',
    keywords: [
      'wall penetration', 'wallpenetration', 'wall sleeve'
    ],
    objectType: 'FLOOR-SYMBOL',
    skey: 'FLOR',
    priority: 80,
    category: 'misc'
  },
  {
    id: 'weld-seam',
    keywords: [
      'welding seam', 'weldingseam', 'weld seam'
    ],
    objectType: 'WELD',
    skey: 'BV',
    priority: 80,
    category: 'misc'
  },
  {
    id: 'bolt-set',
    keywords: [
      'bolt set', 'boltset', 'bolt set assembly'
    ],
    objectType: 'BOLT',
    skey: '',
    priority: 70,
    category: 'misc'
  },
  {
    id: 'reference-dimension',
    keywords: [
      'reference dimension', 'referencedimension'
    ],
    objectType: 'REFERENCE-DIMENSION',
    skey: '',
    priority: 70,
    category: 'misc'
  },
  {
    id: 'reference-point',
    keywords: [
      'reference point', 'referencepoint'
    ],
    objectType: 'REFERENCE-POINT',
    skey: '',
    priority: 70,
    category: 'misc'
  },
  {
    id: 'iso-split-point',
    keywords: [
      'iso split point', 'iso splitpoint', 'system breaker', 'systembreaker'
    ],
    objectType: 'ISO-SPLIT-POINT',
    skey: '',
    priority: 75,
    category: 'misc'
  },
  {
    id: 'end-connection-equipment',
    keywords: [
      'end connection equipment', 'nozzle'
    ],
    objectType: 'END-CONNECTION-EQUIPMENT',
    skey: '',
    priority: 70,
    category: 'misc'
  },
  {
    id: 'gasket',
    keywords: [
      'gasket', 'ring gasket', 'spiral wound', 'flat gasket',
      'dichtung', 'flachdichtung',
      'прокладка', 'уплотнение',
      'junta', 'empaquetadura',
      'joint', 'garniture',
      'tömítés',
      '垫片', '密封垫'
    ],
    objectType: 'GASKET',
    skey: '',
    priority: 50,
    category: 'misc'
  },
  {
    id: 'expansion-bellows',
    keywords: [
      'expansion joint', 'bellows', 'expansion bellows', 'compensator', 'flexible joint',
      'kompensator', 'dehnungsausgleicher', 'balg',
      'компенсатор', 'сильфон', 'гибкое соединение',
      'junta de expansión', 'compensador', 'fuelle',
      'joint de dilatation', 'compensateur', 'soufflet',
      '膨胀节', '补偿器', '波纹管'
    ],
    objectType: 'MISC-COMPONENT',
    skey: 'EX??',
    priority: 75,
    category: 'misc'
  },
  {
    id: 'spectacle-blind',
    keywords: [
      'spectacle blind', 'figure 8', 'spec blind', 'figure eight',
      'brillenblind', 'wechselblind',
      'очковая заглушка', 'поворотная заглушка',
      'ciego espectáculo', 'figura 8',
      'lunettes', 'obturateur à lunettes',
      '8字盲板', '眼镜盲板'
    ],
    objectType: 'MISC-COMPONENT',
    skey: 'SP',
    priority: 75,
    category: 'misc'
  },
  {
    id: 'spectacle-blind-open',
    keywords: [
      'open spectacle', 'spectacle open', 'open blind',
      'offene brillenblind',
      'открытая заглушка',
      'ciego abierto',
      'lunettes ouvertes',
      '开式眼镜盲板'
    ],
    objectType: 'MISC-COMPONENT',
    skey: 'SB',
    priority: 80,
    category: 'misc'
  },
  {
    id: 'spacer',
    keywords: [
      'spacer', 'spool piece', 'pup piece', 'spacer ring',
      'passstück', 'zwischenstück', 'distanzstück',
      'катушка', 'вставка', 'проставка',
      'espaciador', 'carrete', 'pieza separadora',
      'intercalaire', 'manchette', 'entretoise',
      '短管', '插入件', '隔环'
    ],
    objectType: 'MISC-COMPONENT',
    skey: 'SR',
    priority: 55,
    category: 'misc'
  },
  {
    id: 'plug-pipe',
    keywords: [
      'plug', 'pipe plug', 'hex plug', 'square plug',
      'stopfen', 'verschlussstopfen',
      'пробка', 'заглушка резьбовая',
      'tapón', 'tapón macho',
      'bouchon', 'obturateur',
      '管塞', '堵头'
    ],
    objectType: 'MISC-COMPONENT',
    skey: 'PL',
    priority: 65,
    category: 'misc'
  },
  {
    id: 'plug-male-blanking',
    keywords: [
      'blanking plug', 'male plug', 'blind plug',
      'blindstopfen', 'verschlussstopfen',
      'глухая пробка',
      'tapón ciego',
      'bouchon mâle',
      '盲堵'
    ],
    objectType: 'MISC-COMPONENT',
    skey: 'BM',
    priority: 70,
    category: 'misc'
  },
  {
    id: 'non-catalog',
    keywords: [
      'non-catalog', 'non catalog', 'special item', 'custom',
      'sonderanfertigung', 'nicht katalog',
      'нестандартный', 'специальный',
      'no catálogo', 'especial',
      'hors catalogue', 'spécial',
      '非标', '特殊件'
    ],
    objectType: 'MISC-COMPONENT',
    skey: 'NC??',
    priority: 40,
    category: 'misc'
  },
  {
    id: 'cross-fitting',
    keywords: [
      'cross', 'cross fitting', '4-way fitting', 'four way fitting',
      'kreuzstück', 'kreuz',
      'крестовина', 'крест',
      'cruz', 'cruceta',
      'croix',
      '四通', '十字'
    ],
    objectType: 'CROSS',
    skey: 'CR??',
    priority: 70,
    category: 'fitting'
  },

  // -------------------------------------------------------------------------
  // LAPJOINT-STUBEND
  // -------------------------------------------------------------------------
  {
    id: 'stub-end',
    keywords: [
      'stub end', 'stub-end', 'stubend', 'lap joint stub',
      'bördel', 'bördelstutzen',
      'бурт', 'отбортовка',
      'extremo biselado', 'stub',
      'collet', 'bout à collerette',
      '翻边短节', '活套法兰短节'
    ],
    objectType: 'LAPJOINT-STUBEND',
    skey: 'FLSE',
    priority: 75,
    category: 'fitting'
  },

  // -------------------------------------------------------------------------
  // REINFORCEMENT PAD
  // -------------------------------------------------------------------------
  {
    id: 'reinforcement-pad',
    keywords: [
      'reinforcement pad', 'reinforcing pad', 'repad', 'rf pad', 'rfpad', 'saddle pad',
      'verstärkungsplatte', 'verstärkungsblech',
      'накладка усиливающая', 'подкладное кольцо',
      'placa de refuerzo', 'refuerzo',
      'plaque de renfort',
      '加强板', '补强板'
    ],
    objectType: 'REINFORCEMENT-PAD',
    skey: '',
    priority: 65,
    category: 'fitting'
  }
];

// ============================================================================
// END-TYPE PATTERNS - Based on PCF Manual Section 9.3
// BV (Beveled/Buttweld), FL (Flanged), SC (Screwed), SW (Socket Weld),
// WF (Wafer Flanged), LAP (Lap Joint), CL (Clamped), TC (Tube Connection),
// GR (Grooved), CP (Compression), UNDEFINED_ET
// ============================================================================

export const END_TYPE_PATTERNS: EndTypePattern[] = [
  {
    keywords: [
      'butt weld', 'buttweld', 'bw', 'bwe', 'butt-weld', 'beveled', 'bevel', 'bv',
      'stumpfschweißen', 'stumpfnaht',
      'стыковая сварка', 'стыковой', 'под сварку',
      'soldadura a tope', 'biselado',
      'soudure bout à bout', 'chanfreiné',
      '对焊', '坡口'
    ],
    endType: 'BW',
    priority: 90
  },
  {
    keywords: [
      'socket weld', 'socketweld', 'sw', 'swe', 'socket-weld',
      'schweißmuffe', 'muffenschweißung',
      'раструбная сварка', 'раструбный', 'под раструбную сварку',
      'soldadura de enchufe', 'enchufe soldado',
      'soudure à emboîtement',
      '承插焊'
    ],
    endType: 'SW',
    priority: 90
  },
  {
    keywords: [
      'threaded', 'screwed', 'sc', 'thd', 'npt', 'bsp', 'scrd',
      'gewinde', 'geschraubt', 'gewindeanschluss',
      'резьба', 'резьбовой', 'нарезной', 'на резьбе',
      'roscado', 'con rosca', 'rosca',
      'fileté', 'taraudé', 'filetage',
      '螺纹', '丝扣'
    ],
    endType: 'SC',
    priority: 85
  },
  {
    keywords: [
      'flanged', 'fl', 'flg', 'flange end', 'flange connection',
      'geflancht', 'mit flansch', 'flanschanschluss',
      'фланцевый', 'с фланцами', 'фланец',
      'bridado', 'con brida', 'embridado',
      'à brides', 'bridé',
      '法兰', '法兰连接'
    ],
    endType: 'FL',
    priority: 85
  },
  {
    keywords: [
      'wafer', 'wafer flange', 'wf', 'wafer style', 'lug type',
      'zwischenflansch', 'waferflansch',
      'межфланцевый', 'бесфланцевый',
      'tipo wafer', 'oblea',
      'type wafer', 'entre-brides',
      '对夹', '夹持式'
    ],
    endType: 'WF',
    priority: 85
  },
  {
    keywords: [
      'lap joint', 'lap', 'loose flange', 'van stone',
      'losflansch', 'bördelflansch',
      'свободный фланец', 'накидной',
      'junta solapada', 'brida loca',
      'bride tournante', 'à collerette',
      '活套', '松套法兰'
    ],
    endType: 'LAP',
    priority: 80
  },
  {
    keywords: [
      'clamped', 'clamp', 'cl', 'tri-clamp', 'sanitary clamp',
      'klemme', 'klemmverbindung', 'spannring',
      'хомут', 'зажимной', 'клэмп',
      'abrazadera', 'sujetado', 'clamp sanitario',
      'collier', 'serrage', 'clamp',
      '卡箍', '卡盘'
    ],
    endType: 'CL',
    priority: 80
  },
  {
    keywords: [
      'tube connection', 'tc', 'tube fitting', 'tube end',
      'rohranschluss', 'rohrverschraubung',
      'трубное соединение', 'трубный',
      'conexión de tubo', 'racor de tubo',
      'raccord tube',
      '管接头'
    ],
    endType: 'TC',
    priority: 75
  },
  {
    keywords: [
      'grooved', 'gr', 'victaulic', 'mechanical joint', 'groove',
      'rillen', 'nutverbindung', 'rillenverbindung',
      'желобчатый', 'муфтовый', 'виктаулик',
      'ranurado', 'acanalado', 'victaulic',
      'rainuré', 'à gorge',
      '沟槽', '卡箍式'
    ],
    endType: 'GR',
    priority: 80
  },
  {
    keywords: [
      'compression', 'comp', 'cp', 'ferrule', 'bite type',
      'klemmring', 'schneidring', 'klemmverschraubung',
      'обжим', 'врезное кольцо', 'компрессионный',
      'compresión', 'férula', 'anillo de presión',
      'compression', 'bague', 'olive',
      '卡套', '压缩式'
    ],
    endType: 'CP',
    priority: 75
  },
  {
    keywords: [
      'plain end', 'pe', 'smooth end',
      'glattende', 'glattes ende',
      'гладкий конец', 'без обработки',
      'extremo liso', 'sin preparación',
      'bout lisse', 'extrémité lisse',
      '平口'
    ],
    endType: 'PE',
    priority: 70
  },
  {
    keywords: [
      'push fit', 'push-fit', 'press fit', 'crimp', 'press connection',
      'steckverbindung', 'pressverbindung',
      'пресс-фитинг', 'обжимной', 'пресс-соединение',
      'pressfitting', 'conexión a presión',
      'raccord à sertir', 'sertissage',
      '压接'
    ],
    endType: 'PF',
    priority: 70
  },
  {
    keywords: [
      'glued', 'gl', 'solvent weld', 'cemented',
      'geklebt', 'klebverbindung',
      'клеевое', 'склеенный',
      'pegado', 'cementado',
      'collé', 'soudure chimique',
      '粘接'
    ],
    endType: 'GL',
    priority: 70
  },
  {
    keywords: [
      'welded', 'weld', 'wld',
      'geschweißt', 'schweißung',
      'сварной', 'сварка',
      'soldado', 'soldadura',
      'soudé',
      '焊接'
    ],
    endType: 'BW',
    priority: 50
  }
];

// ============================================================================
// ANGLE PATTERNS for extracting numeric values
// ============================================================================

export const ANGLE_REGEX = /(\d+)\s*(°|deg|degree|grad|градус)?/gi;
export const SIZE_REGEX = /(\d+(?:\.\d+)?)\s*("|inch|in|mm|dn|nb)?/gi;

/**
 * Common angles used in piping - for validation
 */
export const COMMON_ANGLES = [15, 22.5, 30, 45, 60, 90, 180];

/**
 * Get pattern priority multiplier based on keyword match quality
 */
export function getMatchQualityMultiplier(
  keyword: string,
  familyName: string,
  isExactMatch: boolean
): number {
  if (isExactMatch) return 1.5;

  // Word boundary match is better than substring
  const wordBoundaryRegex = new RegExp(`\\b${keyword}\\b`, 'i');
  if (wordBoundaryRegex.test(familyName)) return 1.2;

  return 1.0;
}
